import { useState, useEffect, useMemo } from 'react';
import { questions as allQuestions } from '../data/questions';
import QuizCard from '../components/QuizCard';
import QuizResults from '../components/QuizResults';
import { Timer, AlertCircle, Send } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export default function Quiz() {
  const { t } = useLanguage();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Array<number[]>>([]);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);

  // Randomly select questions when quiz starts
  const randomQuestions = useMemo(() => {
    if (!quizStarted) return [];
    const shuffled = [...allQuestions].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 10); // Take only 10 random questions
  }, [quizStarted]);

  // Initialize answers array with empty arrays
  useEffect(() => {
    if (quizStarted && randomQuestions.length > 0) {
      setAnswers(new Array(randomQuestions.length).fill([]));
    }
  }, [quizStarted, randomQuestions.length]);

  // Timer effect
  useEffect(() => {
    let interval: number;
    if (quizStarted && !quizCompleted) {
      interval = setInterval(() => {
        setTimeElapsed((prev) => prev + 1);
      }, 1000) as unknown as number;
    }
    return () => clearInterval(interval);
  }, [quizStarted, quizCompleted]);

  const startQuiz = () => {
    setQuizStarted(true);
    setTimeElapsed(0);
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setQuizCompleted(false);
  };

  const handleAnswerSelect = (selectedIndices: number[]) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = selectedIndices;
    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQuestionIndex < randomQuestions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const calculateScore = () => {
    // Filter out unanswered questions
    const attemptedQuestions = randomQuestions.filter((_, index) => answers[index].length > 0);
    const attemptedAnswers = answers.filter(answer => answer.length > 0);
    setQuizCompleted(true);
  };

  const restartQuiz = () => {
    setQuizStarted(false);
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setQuizCompleted(false);
    setTimeElapsed(0);
  };

  if (!quizStarted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('quiz.title')}
          </h1>
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <AlertCircle className="w-12 h-12 text-purple-600 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold mb-4">{t('quiz.beforeStart')}</h2>
            <ul className="text-left text-gray-600 space-y-2 mb-6">
              <li>• {t('quiz.totalQuestions', { count: '10' })}</li>
              <li>• {t('quiz.difficultyLevels')}</li>
              <li>• {t('quiz.multipleAnswers')}</li>
              <li>• {t('quiz.navigation')}</li>
              <li>• {t('quiz.timing')}</li>
            </ul>
            <button
              onClick={startQuiz}
              className="bg-purple-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
            >
              {t('quiz.start')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (quizCompleted) {
    return (
      <QuizResults
        questions={randomQuestions}
        userAnswers={answers}
        timeElapsed={timeElapsed}
        onRestart={restartQuiz}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="text-gray-600">
            {t('quiz.question')} {currentQuestionIndex + 1} {t('quiz.of')} {randomQuestions.length}
          </div>
          <div className="flex items-center text-gray-600">
            <Timer className="w-5 h-5 mr-2" />
            {Math.floor(timeElapsed / 60)}:{(timeElapsed % 60).toString().padStart(2, '0')}
          </div>
        </div>

        {randomQuestions.length > 0 && (
          <QuizCard
            question={randomQuestions[currentQuestionIndex]}
            selectedAnswers={answers[currentQuestionIndex] || []}
            onAnswerSelect={handleAnswerSelect}
          />
        )}

        <div className="mt-6 flex justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className={`px-8 py-3 rounded-lg font-medium transition-colors ${
              currentQuestionIndex === 0
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-gray-600 text-white hover:bg-gray-700'
            }`}
          >
            {t('navigation.previous')}
          </button>

          <div className="flex gap-4">
            <button
              onClick={handleNext}
              disabled={currentQuestionIndex === randomQuestions.length - 1}
              className={`px-8 py-3 rounded-lg font-medium transition-colors ${
                currentQuestionIndex === randomQuestions.length - 1
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700'
              }`}
            >
              {t('navigation.next')}
            </button>

            <button
              onClick={calculateScore}
              className="px-8 py-3 rounded-lg font-medium bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              {t('quiz.submit')}
            </button>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex flex-wrap gap-2">
            {randomQuestions.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentQuestionIndex(index)}
                className={`w-10 h-10 rounded-lg flex items-center justify-center font-medium transition-colors ${
                  currentQuestionIndex === index
                    ? 'bg-purple-600 text-white'
                    : answers[index]?.length > 0
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}